import { CurrentUser } from '@appTypes/CurrentUser';
import { DealerState } from '@appTypes/DealerState';
import {
  AcquirePermissions,
  AcquirePermissionsData,
} from '@components/market-conquest/permissions/AcquirePermissions';
import { TabItemKeysType } from '@components/market-conquest/tabs/utils';
import { useCommon } from '@hooks/common/UseCommon';
import { AppState } from '@store/AppState';
import { MarketConquestState } from '@store/market-conquest/MarketConquestReducer';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const getInitialTab = (isMapAvailable: boolean, isPortfolioAvailable: boolean): TabItemKeysType => {
  if (isMapAvailable) {
    if (isPortfolioAvailable) {
      return 'portfolio';
    } else {
      return 'map';
    }
  } else if (isPortfolioAvailable) {
    return 'portfolio';
  }
  return null;
};

const useMarketConquest = () => {
  const { featureToggle, marketConquestStatus, dealerInfos } = useSelector<AppState, DealerState>(
    (state) => state.dealer,
  );
  const { isLoading } = useSelector<AppState, MarketConquestState>((state) => state.marketConquest);
  const [selectedTab, setSelectedTab] = useState<TabItemKeysType | null>(null);
  const { IsIncentiveAdvantagePackageType } = useCommon();

  const {
    cqOrbeeUiEnhancements: ftCqOrbeeUiEnhancementsEnabled,
    cqMap: ftIsMapAvailable,
    cqMarketplace: ftIsMarketplaceAvailable,
  } = featureToggle;

  const { permissions, isPrimaryUser } = useSelector<AppState, CurrentUser>(
    (state) => state.currentUser,
  );

  const permissionsData: AcquirePermissionsData = {
    isAutomotiveUser: isPrimaryUser,
    hasAtLeasOneCqSub: marketConquestStatus,
    hasAcquirePermission: permissions.includes('accessConquestPage'),
    hasMarketplacePermission: permissions.includes('accessOpportunityMapAndMarketplace'),
    hasIAPackage: IsIncentiveAdvantagePackageType(dealerInfos[0]?.packageType),
    ftIsMarketplaceAvailable,
    ftIsMapAvailable,
  };

  const acquirePermissions = new AcquirePermissions(permissionsData).getPermissions();
  const cqOrbeeUiEnhancementsEnabled = ftCqOrbeeUiEnhancementsEnabled;

  useEffect(() => {
    setSelectedTab(
      getInitialTab(acquirePermissions.displayOpportunityMap, acquirePermissions.displayProspect),
    );
  }, [
    acquirePermissions.displayOpportunityMap,
    acquirePermissions.displayProspect,
    setSelectedTab,
  ]);

  const tabValues = useMemo(
    () => ({
      selectedKey: selectedTab,
      setSelectedKey: setSelectedTab,
      isMapAvailable: acquirePermissions.displayOpportunityMap,
      isPortfolioAvailable: acquirePermissions.displayProspect,
      isMarketplaceAvailable: acquirePermissions.displayMarketplace,
    }),
    [
      selectedTab,
      acquirePermissions.displayOpportunityMap,
      acquirePermissions.displayProspect,
      acquirePermissions.displayMarketplace,
    ],
  );

  return {
    cqOrbeeUiEnhancementsEnabled,
    isLoading,
    selectedTab,
    tabValues,
    permissions: acquirePermissions,
  } as const;
};

export { useMarketConquest };
